import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import type { FormInputs } from "./form-validation/user-login.schema";
import { schema, defaultValues } from "./form-validation/user-login.schema";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Eye, Loader2, EyeOff, LogIn } from "lucide-react";
import type { RequestState } from "src/redux/state/shared-types";
import clsx from "clsx";
import { ThemedButton } from "@shared-kernel/primary/shared/themed-button/themed-button";

interface Props {
  onSubmit: (formBody: FormInputs) => void;
  onDisplayResetPasswordForm: () => void;
  processing: RequestState;
}

const LoginForm = ({ onSubmit, onDisplayResetPasswordForm, processing }: Props) => {
  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const {
    formState: { isDirty },
  } = methods;

  const EyeIcon = showPassword ? Eye : EyeOff;

  const LoginIcon = processing === "pending" ? Loader2 : LogIn;

  return (
    <Form {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="space-y-2">
          <FormField
            control={methods.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input {...field} type="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mot de passe</FormLabel>
                <FormControl>
                  <div className="relative">
                    <EyeIcon
                      className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    />
                    <Input {...field} type={showPassword ? "text" : "password"} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex items-center justify-between gap-x-2 pt-2">
            <div className="w-[100px] lg:w-auto">
              <a href="#link" onClick={onDisplayResetPasswordForm} role="button" className="text-sm font-bold hover:font-semibold">
                Mot de passe oublié?
              </a>
            </div>
            <ThemedButton isDirty={isDirty}>
              <LoginIcon className={clsx("mr-2 size-4", processing === "pending" && "animate-spin")} />
              Se connecter
            </ThemedButton>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default LoginForm;
